import clsx from "clsx";
import { Pencil, Plus, Search, Sparkles, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "wouter";
import { FixHere } from "../../web/components/FixHere";
import { Badge, DraftBar, ErrorNotice, SectionCard, type Tone } from "../../web/components/ui";
import { can, usePriceListSaver } from "./data";
import { isProduct, type MaterialRow, meta, type PriceListData, type TagRow, type TagSetRow, type Wording } from "./meta";
import { NamePreview } from "./NameTags";
import { matchesSearch } from "./ListToolbar";
import { inOrder, oneOnlyClash, orderedTags, smartName, tagsOf, wordingText } from "./smartTags";
import { tagChoicesFor, tagSources } from "./productTags";

/*
 * An item's tags (itemTags.ts), on its Details tab, as a card: its tags as chips, Edit tags beside its heading. Editing is a draft (kept by its
 * window while other tabs are open, as its pricing is): every tag, a row per set (pick one: one of its tags at most, the chosen one
 * clicked again for none; any: as many as wanted), those not in a set last; big sets fold up, and many tags get a search. The full name below shows what its name will
 * be; Save writes its tags and that name (in ServiceM8) together. Under them, its warnings along the card's bottom
 * (SmartNameNotice, TagClashNotice).
 *
 * A product's (productTags.ts) are what it is (product tags only); its name is its own (no wording added). An item linked to products has their product tags too, shown as theirs, changed on them:
 * its own are how it's sold.
 */

/**
 * A tag's colour, as its wording reads in a ServiceM8 name (NameTags.tsx ServiceM8Name): in the brackets green, a line under it
 * amber; a label grey.
 */
const WORDING_TONE: Record<Wording["kind"], Tone> = { brackets: "green", includes: "green", excludes: "green", line: "amber" };

/** The same colours for its wording as text. */
export const WORDING_TEXT: Record<Wording["kind"], string> = { brackets: "text-brand-ink", includes: "text-brand-ink", excludes: "text-brand-ink", line: "text-warning-ink" };

/** A tag's chip: its name, coloured by what it puts in the name (that, and its set, on hover). */
export function TagChip({ data, tag, onRemove }: Readonly<{ data: Pick<PriceListData, "tagSets">; tag: TagRow; onRemove?: () => void }>) {
    const set = data.tagSets.find((s) => s.id === tag.setId);
    const title = [set ? `${set.name}${set.oneOnly ? " (pick one)" : ""}` : "", tag.wording ? `In the name: ${wordingText(tag.wording)}` : "A label"]
        .filter(Boolean)
        .join(" · ");
    const tone = tag.wording ? WORDING_TONE[tag.wording.kind] : "grey";
    const inside = (
        <span className="inline-flex items-center gap-1">
            {tag.wording ? <Sparkles className="size-3" aria-label="Adds to the name" /> : null}
            {tag.name}
            {onRemove ? (
                <button type="button" className="-mr-0.5 cursor-pointer rounded-sm opacity-70 hover:opacity-100" aria-label={`Take ${tag.name} off`} onClick={onRemove}>
                    <X className="size-3" />
                </button>
            ) : null}
        </span>
    );
    return (
        <Badge tone={tone} title={title}>
            {inside}
        </Badge>
    );
}

/** Two lists of tag ids the same, in any order. */
export const sameTags = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((id) => b.includes(id));

/** An item's own tags (not its products' or group's), in order. */
const ownTags = (data: Pick<PriceListData, "tags" | "tagSets" | "itemTags">, uuid: string) => orderedTags(data).filter((t) => (data.itemTags[uuid] ?? []).includes(t.id));

/** An item's tags as its editor starts them: its own, a pick-one set's first only (more than one: a conflict, put right on Save). */
export function startingTags(data: Pick<PriceListData, "tags" | "tagSets" | "itemTags">, uuid: string): string[] {
    const ids = ownTags(data, uuid).map((t) => t.id);
    const oneOnly = new Set(data.tagSets.filter((s) => s.oneOnly).map((s) => s.id));
    const seen = new Set<string>();
    return ids.filter((id) => {
        const setId = data.tags.find((t) => t.id === id)?.setId;
        if (!setId || !oneOnly.has(setId)) return true;
        if (seen.has(setId)) return false;
        seen.add(setId);
        return true;
    });
}

/** More than this many in a set: it shows its chosen and the first few, the rest behind "+N more". */
const FOLD_AT = 8;
const FOLDED = 6;
/** More tags than this altogether: a search over them. */
const SEARCH_AT = 15;

export function TagPicker(
    props: Readonly<{
        data: PriceListData;
        /** The item (or bundle) whose tags these are. */
        item: Pick<MaterialRow, "uuid" | "name">;
        /** A bundle's tags (saved as a bundle's, with Manage bundles), not an item's. */
        bundle?: boolean;
        /** Its warnings (SmartNameNotice, TagClashNotice) along the card's bottom, and Show's token. */
        fix?: ReactNode;
        fixToken?: number;
        /** Its tags being edited (ids), kept by its window while other tabs are open; null: not being edited. */
        draft: string[] | null;
        /** Tags to suggest while it's edited (a bundle's: its items' tags, most used first), each one click to add. */
        suggested?: readonly string[];
        onDraft: (draft: string[] | null) => void;
        /** Saved by its tab's save bar with the rest of its details (ItemWindow.tsx DetailsSave), not its own: no save bar here. */
        savedWith?: "details";
    }>,
) {
    const { data, item, draft } = props;
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState("");
    // Sets showing all their tags (those with many fold up).
    const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
    const canEdit = can(data, props.bundle ? "bundles" : "items.edit");
    // Its own (edited here), and those its products give it (changed there).
    const own = ownTags(data, item.uuid);
    const current = own.map((t) => t.id);
    const row = props.bundle ? undefined : data.materials.find((m) => m.uuid === item.uuid);
    const product = row !== undefined && isProduct(row);
    const sources = row ? tagSources(data, item.uuid) : new Map<string, string>();
    const inherited = tagsOf(data, item.uuid).filter((t) => sources.has(t.id));
    // What it can have: a product, product tags; a linked item, the rest (what it is comes from its products).
    const allowed = row ? tagChoicesFor(data, row) : () => true;
    const editing = draft !== null;
    const chosen = draft ?? current;
    const changed = editing && !sameTags(chosen, current);
    // The name its tags give it as they'd be, on its name as typed (Save updates its name to that). A product's name is its own.
    const named = changed && !product ? (smartName({ ...data, itemTags: { ...data.itemTags, [item.uuid]: chosen } }, item) ?? item.name) : item.name;
    // A pick-one set it has more than one tag of: those tags outlined (its warning along the card's bottom says so).
    const clash = oneOnlyClash(data, item.uuid);

    // A tag added to these: a pick-one set's swaps its sibling.
    const adding = (ids: readonly string[], t: TagRow) => {
        const set = data.tagSets.find((s) => s.id === t.setId);
        const siblings = set?.oneOnly ? data.tags.filter((x) => x.setId === set.id).map((x) => x.id) : [];
        return [...ids.filter((id) => !siblings.includes(id)), t.id];
    };
    const toggle = (t: TagRow) => {
        if (!draft) return;
        props.onDraft(draft.includes(t.id) ? draft.filter((id) => id !== t.id) : adding(draft, t));
    };
    // A tag's hover text: its wording.
    const chipTitle = (t: TagRow) => (t.wording ? `In the name: ${wordingText(t.wording)}` : undefined);
    // Along its save bar: what saving does.
    let barMessage = `Choose the tags for this ${props.bundle ? "bundle" : product ? "product" : "item"}.`;
    if (changed) barMessage = named === item.name ? (product ? "Tags changed: items linked to it have them too." : "Tags changed.") : "Saving also updates the name in ServiceM8.";
    // Suggested (its items'), not on it yet.
    const suggestions = (props.suggested ?? []).flatMap((id) => data.tags.filter((t) => t.id === id && !chosen.includes(id)));
    const save = async () => {
        if (!draft) return;
        setBusy(true);
        setError(null);
        try {
            await (props.bundle ? saver.setBundleTags(item.uuid, draft, true) : saver.setItemTags(item.uuid, draft, !product));
            props.onDraft(null);
        } catch (err) {
            setError(err as Error);
        } finally {
            setBusy(false);
        }
    };

    // Editing: every tag, by set (tags not in one last), searched when there are many.
    const searching = query.trim() !== "";
    const matches = (t: TagRow) => matchesSearch(`${t.name} ${t.wording ? wordingText(t.wording) : ""}`, query);
    const order = inOrder(data);
    const groups = [
        ...order.flatMap((e) => (e.set ? [{ set: e.set as TagSetRow | null, tags: e.tags }] : [])),
        { set: null, tags: order.flatMap((e) => (e.set ? [] : e.tags)) },
    ]
        .map((g) => ({ ...g, tags: g.tags.filter(allowed) }))
        .map((g) => ({ ...g, tags: searching ? g.tags.filter((t) => matches(t) || (g.set ? matchesSearch(g.set.name, query) : false)) : g.tags }))
        .filter((g) => g.tags.length);

    const editor = (
        <div className="space-y-3">
            {/* Its items' tags (a bundle's), to add with a click each, or all (a pick-one set's: the most used of its items' wins). */}
            {suggestions.length && canEdit ? (
                <div className="flex flex-wrap items-center gap-1.5 text-sm">
                    <span className="text-muted">Its items have</span>
                    {suggestions.map((t) => (
                        <ToggleChip key={t.id} on={false} tone={t.wording ? WORDING_TONE[t.wording.kind] : "grey"} disabled={!canEdit || busy} title="Add it" onClick={() => toggle(t)}>
                            <Plus className="size-3" />
                            {t.name}
                        </ToggleChip>
                    ))}
                    {suggestions.length > 1 ? (
                        <button
                            type="button"
                            className="link-button text-xs"
                            disabled={busy}
                            onClick={() => draft && props.onDraft([...suggestions].reverse().reduce(adding, draft))}
                        >
                            Add all
                        </button>
                    ) : null}
                </div>
            ) : null}
            {data.tags.length > SEARCH_AT ? (
                <label className="relative block">
                    <span className="sr-only">Search tags</span>
                    <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                    <input className="input pl-9" placeholder="Search tags" value={query} onChange={(e) => setQuery(e.target.value)} />
                </label>
            ) : null}
            {groups.length ? (
                <div className="space-y-2.5">
                    {groups.map((g) => {
                        const key = g.set?.id ?? "";
                        const oneOnly = g.set?.oneOnly ?? false;
                        // Many: its chosen and the first few, the rest folded (searching shows all that match).
                        const folds = !searching && g.tags.length > FOLD_AT && !unfolded.has(key);
                        const shown = folds ? g.tags.filter((t, i) => i < FOLDED || chosen.includes(t.id)) : g.tags;
                        const hidden = g.tags.length - shown.length;
                        return (
                            <div key={key || "other"} className={clsx("grid gap-1 sm:gap-3", props.savedWith ? "sm:grid-cols-[8rem_1fr]" : "sm:grid-cols-[10rem_1fr]")}>
                                <div className={props.savedWith ? "pt-1 text-(length:--label-text)" : "pt-0.5 text-sm"}>
                                    <span className="font-medium">{g.set ? g.set.name : groups.length > 1 ? "Other" : "Tags"}</span>
                                    {oneOnly ? (
                                        <span className="block text-xs text-muted" title="Picking another swaps it; click the chosen one again for none">
                                            Pick one
                                        </span>
                                    ) : null}
                                </div>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {shown.map((t) => (
                                        <ToggleChip
                                            key={t.id}
                                            on={chosen.includes(t.id)}
                                            tone={t.wording ? WORDING_TONE[t.wording.kind] : "grey"}
                                            disabled={!canEdit || busy}
                                            title={chipTitle(t)}
                                            onClick={() => toggle(t)}
                                        >
                                            {t.wording ? <Sparkles className="size-3" aria-label="Adds to the name" /> : null}
                                            {t.name}
                                        </ToggleChip>
                                    ))}
                                    {hidden > 0 ? (
                                        <button type="button" className="link-button text-xs" onClick={() => setUnfolded((was) => new Set([...was, key]))}>
                                            +{hidden.toLocaleString()} more
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="m-0 text-sm text-muted">{searching ? "No tags match." : "No tags yet."}</p>
            )}
        </div>
    );

    // Its action beside its heading (as Supplier prices' Add is); editing, where tags are set up instead.
    const aside =
        editing && props.savedWith && !changed ? (
            <button type="button" className="link-button" onClick={() => props.onDraft(null)}>
                Close
            </button>
        ) : editing ? (
            <>
                Set them up in{" "}
                <Link href={`/settings/${meta.id}#tags`}>Settings › Price List</Link>.
            </>
        ) : canEdit ? (
            <button type="button" className="link-button inline-flex items-center gap-1" onClick={() => props.onDraft(startingTags(data, item.uuid))}>
                <Pencil className="size-3.5" /> Edit tags
            </button>
        ) : undefined;
    const footer =
        editing && !props.savedWith ? (
            <div className={clsx("border-t px-3 py-2", changed ? "border-brand-500/30 bg-brand-500/5" : "border-line")}>
                <DraftBar
                    message={barMessage}
                    busy={busy}
                    error={error}
                    actions={
                        <>
                            <button type="button" className="btn-primary" disabled={busy || !changed} onClick={() => void save()}>
                                Save
                            </button>
                            <button type="button" className="btn-secondary" disabled={busy} onClick={() => props.onDraft(null)}>
                                {changed ? "Cancel" : "Close"}
                            </button>
                        </>
                    }
                />
            </div>
        ) : undefined;
    const body = (
        <>
            {/* Its products': shown as theirs (changed there), outlined. */}
            {inherited.length ? (
                <div className="flex flex-wrap items-center gap-1.5 text-sm">
                    <span className="text-muted">From its products</span>
                    {inherited.map((t) => (
                        <span key={t.id} className={clsx("rounded-full border border-dashed border-line", clash && t.setId === clash.id && "ring-2 ring-warning")} title={`From ${sources.get(t.id) ?? ""}`}>
                            <TagChip data={data} tag={t} />
                        </span>
                    ))}
                </div>
            ) : null}
            {editing ? (
                editor
            ) : (
                <div className="flex flex-wrap items-center gap-1.5">
                    {own.map((t) => (
                        <span key={t.id} className={clsx("rounded-full", clash && t.setId === clash.id && "ring-2 ring-warning")}>
                            <TagChip data={data} tag={t} />
                        </span>
                    ))}
                    {!own.length ? <span className="text-sm text-muted">{inherited.length ? "None of its own." : "No tags."}</span> : null}
                </div>
            )}
            {/* A product's name is its own: tags don't add to it. */}
            {/* A product's name is its own (tags don't add to it); an item's Details tab shows its full name under its name. */}
            {product || props.savedWith ? null : <NamePreview name={named} tags={data.nameTags} label={changed && named !== item.name ? "Full name in ServiceM8, once saved" : undefined} />}
            {editing ? null : <ErrorNotice error={error} />}
        </>
    );
    // Part of an item's Name card (its Details tab): a field, as its name is: its label (and Edit tags) over a box of its tags, its
    // products' with them (dashed, said under it); editing, the same box opens out into every tag by set. Its warnings the card's.
    if (props.savedWith) {
        const ring = (t: TagRow) => clash !== null && t.setId === clash.id && "ring-2 ring-warning";
        const fromProducts = inherited.map((t) => (
            <span key={t.id} className={clsx("rounded-full border border-dashed border-line", ring(t))} title={`From ${sources.get(t.id) ?? ""}`}>
                <TagChip data={data} tag={t} />
            </span>
        ));
        return (
            <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                    <span className="field-label">Tags</span>
                    <span className="text-(length:--label-text) text-muted">{aside}</span>
                </div>
                {editing ? (
                    <div className="space-y-2.5 rounded-(--field-radius) border border-brand-500 bg-surface p-3">
                        {inherited.length ? (
                            <div className="grid gap-1 sm:grid-cols-[8rem_1fr] sm:gap-3">
                                <span className="pt-1 text-(length:--label-text) font-medium">From its products</span>
                                <div className="flex flex-wrap items-center gap-1.5">{fromProducts}</div>
                            </div>
                        ) : null}
                        {editor}
                    </div>
                ) : (
                    <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-(--field-radius) border border-field-edge bg-surface px-(--field-px) py-1.5">
                        {fromProducts}
                        {own.map((t) => (
                            <span key={t.id} className={clsx("rounded-full", ring(t))}>
                                <TagChip data={data} tag={t} />
                            </span>
                        ))}
                        {own.length || inherited.length ? null : <span className="text-sm text-muted">No tags</span>}
                    </div>
                )}
                {inherited.length && !editing ? (
                    <p className="m-0 text-xs text-muted">
                        {inherited.map((t) => t.name).join(", ")} {inherited.length === 1 ? "comes" : "come"} from its products.
                    </p>
                ) : null}
                {editing ? null : <ErrorNotice error={error} />}
            </div>
        );
    }
    return (
        // A card as its pricing and cost are: being edited, tinted with its save bar along its bottom; else its warnings there, so
        // Show brings you to it and flashes it.
        <SectionCard
            label="Tags"
            aside={aside}
            bodyClassName={clsx("space-y-3.5 p-3", changed && "bg-brand-500/5")}
            footer={footer}
            fix={<FixHere attached token={props.fixToken}>{editing ? null : (props.fix ?? null)}</FixHere>}
        >
            {body}
        </SectionCard>
    );
}

/** A tag to pick in the editor: on, filled in its chip's colour; off, an outline in it. The same size either way, so picking one never rewraps a row. */
function ToggleChip(props: Readonly<{ on: boolean; tone?: Tone; disabled?: boolean; title?: string; onClick: () => void; children: ReactNode }>) {
    return (
        <button
            type="button"
            aria-pressed={props.on}
            disabled={props.disabled}
            title={props.title}
            onClick={props.onClick}
            className={clsx(
                "inline-flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap disabled:cursor-default disabled:opacity-60",
                props.on ? "border-current/40" : clsx("bg-surface hover:bg-surface-2", TONE_OFF[props.tone ?? "grey"]),
                props.on && TONE_ON[props.tone ?? "grey"],
            )}
        >
            {props.children}
        </button>
    );
}

/** Off, a faint outline in its colour, so a tag that puts wording in the name (green, amber) stands apart from a label (grey). */
const TONE_OFF: Record<Tone, string> = {
    grey: "border-line text-muted hover:text-ink",
    green: "border-brand-500/40 text-brand-ink/80 hover:text-brand-ink",
    blue: "border-info/40 text-info/80 hover:text-info",
    amber: "border-warning/40 text-warning-ink/80 hover:text-warning-ink",
    red: "border-danger/40 text-danger/80 hover:text-danger",
};

/** On, a chip's colours (as Badge's tones). */
const TONE_ON: Record<Tone, string> = {
    grey: "bg-surface-2 text-ink ring-1 ring-line",
    green: "bg-brand-500/15 text-brand-ink",
    blue: "bg-info-tint text-info",
    amber: "bg-warning-tint text-warning-ink",
    red: "bg-danger-soft text-danger",
};
